import type { MouseEvent } from 'react';
import { STAGE_LABEL, groupProofsByStage, proofsOfEntry, type CasePhoto } from '@/domain';
import { useCasePhotoUrl } from '@/application/usecases/useKits';
import { proofItem } from '@/features/photos/photoViewerItems';
import { openPhotoTile } from '@/features/photos/photoViewerStore';
import { formatInstant } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { CameraIcon } from '@/ui/icons';
import styles from './WorkProofStrip.module.css';

/**
 * The proofs of finished work on a case, each with who took it and when, in
 * lab time. Arranged apart from the case photographs, which mean something
 * else: one is what the case looked like when it came in, the other a record
 * of work done. A strip on the job view and at Final Check; grouped by stage on
 * the record, so it reads down the case the way the case went. Section 2.4 of
 * the request of 13 September 2026.
 */
export function WorkProofStrip({
  proofs,
  grouped = false,
  jobOrderNo,
}: {
  proofs: readonly CasePhoto[];
  grouped?: boolean;
  /** The case, for the name of a photograph somebody downloads from the viewer. */
  jobOrderNo?: string;
}) {
  if (grouped) {
    const groups = groupProofsByStage(proofs);
    // One run of photographs across every stage, in the order the card shows them.
    const shown = groups.flatMap((group) => group.proofs);
    const items = shown.map((proof) => proofItem(proof));

    return (
      <div className={styles.groups} data-photo-tiles="">
        {groups.map((group) => (
          <div key={group.stage}>
            <h5 className={styles.stage}>{STAGE_LABEL[group.stage]}</h5>
            <div className={styles.strip}>
              {group.proofs.map((proof) => (
                <ProofThumb
                  key={proof.id}
                  proof={proof}
                  showStage={false}
                  onOpen={(event) => openPhotoTile(event, items, shown.indexOf(proof), jobOrderNo)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = proofs.map((proof) => proofItem(proof));

  return (
    <div className={styles.strip} data-photo-tiles="">
      {proofs.map((proof, index) => (
        <ProofThumb
          key={proof.id}
          proof={proof}
          onOpen={(event) => openPhotoTile(event, items, index, jobOrderNo)}
        />
      ))}
    </div>
  );
}

/**
 * The photographs of one stage visit, under the line that says they were
 * taken: the proof row in Lab Notes and on the notes card of a case. The
 * request of 5 October 2026 (third), section 6.
 *
 * The row is sent to everybody on the case; the photographs are the caller's
 * own, or everybody's for a holder of seeWorkProof, as the server decides. So
 * this draws nothing at all for a row that has no stage visit, and nothing for
 * a caller who was given none of its photographs. The tiles carry no caption:
 * the row they sit in already says who, when and where.
 */
export function EntryProofs({
  proofs,
  historyEntryId,
  jobOrderNo,
  className,
}: {
  /** Every proof the caller was given for the case; undefined while they load. */
  proofs: readonly CasePhoto[] | undefined;
  historyEntryId: string | null | undefined;
  jobOrderNo?: string;
  className?: string;
}) {
  if (proofs === undefined || historyEntryId == null) return null;
  const shown = proofsOfEntry(proofs, historyEntryId);
  if (shown.length === 0) return null;
  const items = shown.map((proof) => proofItem(proof));

  return (
    <div
      className={className === undefined ? styles.entry : `${styles.entry} ${className}`}
      data-photo-tiles=""
    >
      {shown.map((proof, index) => (
        <ProofThumb
          key={proof.id}
          proof={proof}
          caption={false}
          onOpen={(event) => openPhotoTile(event, items, index, jobOrderNo)}
        />
      ))}
    </div>
  );
}

/**
 * One proof: a button that opens it in the image viewer, with the rest of the
 * strip to swipe through. The request of 5 October 2026 (third), section 6;
 * it was a link that opened the file in a new tab.
 *
 * The bytes come through the port, never an authenticated URL on an `<img>`,
 * exactly as a case photograph does; while they load, and if they fail, the
 * tile is a frame with the caption, which is the fact that matters, and it
 * opens the viewer all the same.
 */
export function ProofThumb({
  proof,
  showStage = true,
  caption = true,
  onOpen,
}: {
  proof: CasePhoto;
  showStage?: boolean;
  /** Who, when and where under the picture. Off where the row around the tile says it. */
  caption?: boolean;
  /** Opens the viewer on the strip this proof is in. By itself when the caller has no strip. */
  onOpen?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const objectUrl = useCasePhotoUrl(proof.thumbnailUrl);
  const item = proofItem(proof);

  return (
    <button
      type="button"
      className={styles.photo}
      aria-label={`${strings.viewer.open(item.caption)}, ${strings.proof.takenBy} ${proof.uploadedByUserName}`}
      onClick={onOpen ?? ((event) => openPhotoTile(event, [item], 0))}
      data-photo-tile={proof.id}
    >
      {objectUrl === null ? (
        <span className={styles.blank} aria-hidden="true" data-photo-frame="">
          <CameraIcon />
        </span>
      ) : (
        <img src={objectUrl} alt="" draggable={false} data-photo-frame="" />
      )}
      {caption && (
        <span className={styles.caption}>
          <b>{proof.uploadedByUserName}</b>
          <span>{formatInstant(proof.uploadedAt)}</span>
          {showStage && <span>{STAGE_LABEL[proof.stage]}</span>}
        </span>
      )}
    </button>
  );
}
